import { useEffect, useState } from 'react'
import { keyboardInset } from 'lib/community/writer-phone'

export interface ViewportInsets {
  /** The on-screen keyboard's height over the layout viewport's bottom (0 without one). */
  bottom: number
  /** How far the visible area is scrolled inside the layout viewport (iOS with a keyboard). */
  top: number
}

const NONE: ViewportInsets = { bottom: 0, top: 0 }

/**
 * The phone writer's fixed bars follow the visible area: the toolbar sits on the keyboard, the
 * top bar on the visible top (`visualViewport` resize + scroll, one update per frame). Without
 * `visualViewport` both stay 0 and the bars keep their CSS places (bottom: 0 + safe area, top: 0).
 * Browser-only: {0, 0} in the server render and the first client render.
 */
export function useViewportInsets(): ViewportInsets {
  const [insets, setInsets] = useState<ViewportInsets>(NONE)
  useEffect(() => {
    const vv = window.visualViewport
    if (!vv) {
      return undefined
    }
    let frame = 0
    const measure = (): void => {
      frame = 0
      const bottom = keyboardInset(window.innerHeight, vv.height, vv.offsetTop)
      const top = Math.max(0, Math.round(vv.offsetTop))
      setInsets(prev => (prev.bottom === bottom && prev.top === top ? prev : { bottom, top }))
    }
    const schedule = (): void => {
      if (!frame) {
        frame = window.requestAnimationFrame(measure)
      }
    }
    vv.addEventListener('resize', schedule)
    vv.addEventListener('scroll', schedule)
    measure()
    return () => {
      vv.removeEventListener('resize', schedule)
      vv.removeEventListener('scroll', schedule)
      if (frame) {
        window.cancelAnimationFrame(frame)
      }
    }
  }, [])
  return insets
}
